/**
 * MCP Gateway API Service
 */

const MCP_API_URL = import.meta.env.VITE_MCP_API_URL || 'http://localhost:8003'

/**
 * Helper function to make authenticated requests to MCP Gateway
 * Uses fetch directly instead of api client since MCP is on a different domain
 */
async function mcpFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('access_token')
  const response = await fetch(`${MCP_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options?.headers,
    },
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }))
    const message = typeof error.detail === 'string' ? error.detail : 'An error occurred'
    throw new Error(message)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

export interface MCPServer {
  id: string
  server_key: string
  name: string
  description: string | null
  icon_url: string | null
  category: string
  tags: string[]
  config_schema: Record<string, any>
  supported_tools: string[]
  is_active: boolean
  plan_required: string
  /** True when the gateway has OAuth app credentials for this provider —
   *  the UI can offer "Connect with <name>" alongside the token form. */
  oauth_enabled: boolean
  created_at: string
  updated_at: string
}

export interface MCPConnection {
  id: string
  tenant_id: string
  created_by: string
  server_key: string
  connection_name: string
  /** 'token' (manually entered) or 'oauth' (connect flow) */
  auth_type: string
  status: string
  last_test_at: string | null
  last_test_status: string | null
  error_message: string | null
  last_used_at: string | null
  usage_count: number
  is_shared_with_team: boolean
  created_at: string
  updated_at: string
}

export interface MCPConnectionCreate {
  server_key: string
  connection_name: string
  config: Record<string, any>
  is_shared_with_team?: boolean
}

export interface MCPConnectionTestResult {
  success: boolean
  message: string
  details?: Record<string, any>
}

/**
 * Fetch all available MCP servers (public endpoint)
 */
export async function fetchMCPServers(category?: string): Promise<MCPServer[]> {
  const query = category ? `?category=${category}` : ''
  return await mcpFetch<MCPServer[]>(`/api/v1/mcp/servers${query}`)
}

/**
 * Fetch a specific MCP server by key
 */
export async function fetchMCPServer(serverKey: string): Promise<MCPServer> {
  return await mcpFetch<MCPServer>(`/api/v1/mcp/servers/${serverKey}`)
}

/**
 * Fetch user's MCP connections
 */
export async function fetchMCPConnections(serverKey?: string): Promise<MCPConnection[]> {
  const query = serverKey ? `?server_key=${serverKey}` : ''
  return await mcpFetch<MCPConnection[]>(`/api/v1/mcp/connections${query}`)
}

/**
 * Create a new MCP connection
 */
export async function createMCPConnection(data: MCPConnectionCreate): Promise<MCPConnection> {
  return await mcpFetch<MCPConnection>(`/api/v1/mcp/connections`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Test an MCP connection
 */
export async function testMCPConnection(connectionId: string): Promise<MCPConnectionTestResult> {
  return await mcpFetch<MCPConnectionTestResult>(`/api/v1/mcp/connections/${connectionId}/test`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

/**
 * Delete an MCP connection
 */
export async function deleteMCPConnection(connectionId: string): Promise<void> {
  await mcpFetch<void>(`/api/v1/mcp/connections/${connectionId}`, {
    method: 'DELETE',
  })
}

/**
 * Begin the OAuth "Connect with <provider>" flow. Returns the provider
 * authorization URL — navigate the browser there; the gateway redirects
 * back to `returnUrl` with ?integration_connected=<id> or ?integration_error=.
 */
export async function startOAuthConnect(
  serverKey: string,
  returnUrl: string,
  options?: { connectionName?: string; isSharedWithTeam?: boolean; reconnectConnectionId?: string }
): Promise<string> {
  const result = await mcpFetch<{ authorization_url: string }>(
    `/api/v1/oauth/${serverKey}/authorize`,
    {
      method: 'POST',
      body: JSON.stringify({
        return_url: returnUrl,
        connection_name: options?.connectionName || null,
        is_shared_with_team: options?.isSharedWithTeam ?? false,
        reconnect_connection_id: options?.reconnectConnectionId || null,
      }),
    }
  )
  return result.authorization_url
}
